import * as React from "react";
import TooltipOptions from "@bizform/primereact/components/tooltip/TooltipOptions";
import { Static } from "@bizform/type/decorator/Static.decorator";
import {
    BaseStateModel,
    ComponentElement,
    ComponentElementDisplayMode,
    ComponentElementStatic,
    ComponentEventType
} from "@bizform/type/model/component_element/ComponentElement.model";
import {
    ComponentCategory,
    ComponentElementManifest,
    ComponentElementProperty
} from "@bizform/type/model/component_element/ComponentElementManifest.model";
import { DataType } from "@bizform/type/model/model/DataType.model";
import { ChipsProps, ComponentHelper, Debug, ObjectHelper } from "@bizform/helper/dist/js/bizflow/helper.min";
import { ComponentElementClass, ReactComponentElementInit } from "@bizform/core/dist/js/bizflow/core.min";
import { AbstractComponentElement } from "../AbstractComponentElement";
import { MultipleInputMask } from "../../misc/addon/inputmask/MultipleInputMask";
import { I18n } from "../../misc/model/I18n.model";


type Props = ChipsProps & {
    mask?: string;
};

type WrappedCompState = {
    // attribute
    name?: string;
    tabindex?: number;
    placeholder?: string;
    value?: string[];
    max?: number;
    disabled?: boolean;
    style?: object;
    className?: string;
    printStyle?: object;
    printClassName?: string;
    tooltipAttribute?: TooltipOptions & {text?: string};
    mask?: string;
    useUnmask?: boolean;
    // event
    onChange?(e: ComponentEventType): void;
    itemTemplate?(item: any): JSX.Element | undefined;
    onAdd?(e: ComponentEventType): void;
    onRemove?(e: ComponentEventType): void;
    focusInput?(e: ComponentEventType): void;
};

type WrappedFormState = {
    onChange?: string;
    onRemove?: string;
    onAdd?: string;
};

type State = BaseStateModel & {
    wrappedCompState: WrappedCompState;
    wrappedFormState: WrappedFormState;
};

/**
 * BfInputMaskMultiple component element
 *
 * @since 0.0.1
 * @author Hwansoon Park, modified by Chris
 * @class
 */
@ComponentElementClass()
@Static<ComponentElementStatic<Array<string | number>>>()
export class BfInputMaskMultiple extends AbstractComponentElement<Props, State> {
    /* manifest */
    static readonly componentElementManifest: ComponentElementManifest = {
        uniqueId: "_SUnbpKD2qzDN4iSuXn5kLdDDkJfNmky",
        category: ComponentCategory.INPUT,
        label: I18n.componentElement.inputmaskMultiple.label,
        description: I18n.componentElement.inputmaskMultiple.description,
        icon: "",
        dataType: [DataType.STRING, DataType.NUMBER, DataType.INTEGER],
        onlyArray: true,
        pathToLimitDataTypeArray: "max",
        property: null
    };

    focus(): void {
        if (this._multipleInputMask && this._multipleInputMask.input) {
            this._multipleInputMask.input.focus();
        }
    }

    setProperty(property: ComponentElementProperty): void {
        try {
            const newState = ComponentHelper.createStateWithProperties<State, ComponentEventType>(
                property,
                this);
            newState.wrappedCompState.max = !newState.wrappedCompState.max ? 1 : newState.wrappedCompState.max;
            newState.wrappedCompState.max *= 1;
            if (newState.wrappedCompState.value) {
                if (newState.wrappedCompState.value.length > newState.wrappedCompState.max) {
                    newState.wrappedCompState.value = newState.wrappedCompState.value.slice(0, newState.wrappedCompState.max);
                    this.setState(newState, this._callbackSetState);
                }
            }

            this.setState(newState);
        } catch (e) {
            this.emitDidCatch(e);
        }
    }

    emit(value: Array<string | number>): void {
    }

    emitFormState(value: string): void {
    }

    emitSubscriptionReject(): void {
    }

    subscription(value: Array<string | number>): void {
        let newState = {
            wrappedCompState: {
                ...this.state.wrappedCompState,
                value: []
            }
        };
        if (ComponentHelper.isNull(value)) {
            this.setState(newState);
        } else if (Array.isArray(value)) {
            let result = [];
            if (this.dataType && (this.dataType.indexOf(DataType.NUMBER) > -1 || this.dataType.indexOf(DataType.INTEGER) > -1)) {
                result = value.map((x, i) => {
                    if (this._multipleInputMask && this._multipleInputMask.input) {
                        this._multipleInputMask.input.value = x;
                        this._multipleInputMask.checkVal();
                        let data = String(x);
                        let inputValue = String(this._multipleInputMask.input.value);
                        for (let i = 0; i < data.length; i++) {
                            if (inputValue.includes(data[i])) {
                                inputValue = inputValue.substring(0, inputValue.indexOf(data[i])) + inputValue.substring(inputValue.indexOf(data[i]) + 1);
                            } else {
                                return x;
                            }
                        }
                        return this._multipleInputMask.input.value;
                    } else {
                        // print mode
                        return x;
                    }
                });
            } else {
                if (this.state.wrappedCompState.useUnmask) {
                    if (this._multipleInputMask && this._multipleInputMask.input) {
                        result = value.map((x, i) => {
                            this._multipleInputMask.input.value = x;
                            this._multipleInputMask.checkVal();
                            let data = String(x);
                            let inputValue = String(this._multipleInputMask.input.value);
                            for (let i = 0; i < data.length; i++) {
                                if (inputValue.includes(data[i])) {
                                    inputValue = inputValue.substring(0, inputValue.indexOf(data[i])) + inputValue.substring(inputValue.indexOf(data[i]) + 1);
                                } else {
                                    return x;
                                }
                            }
                            return this._multipleInputMask.input.value;
                        });
                    }
                } else {
                    result = ObjectHelper.jsonCloneDeep(value);
                }
            }
            newState = {
                wrappedCompState: {
                    ...this.state.wrappedCompState,
                    value: result
                }
            };
            this.setState(newState);
        } else {
            this.emitSubscriptionReject();
        }
    }

    @ReactComponentElementInit(BfInputMaskMultiple)
    static init(): ComponentElement<Array<string | number>> {
        return;
    }

    initDone(): void {
    }

    destroy(): void {
        this.destroyDone();
    }

    destroyDone(): void {
    }

    /* for React */
    props: Props;
    state: State;

    private _multipleInputMask: any;
    private _tooltip: Array<any> = [];

    constructor(props?: Props) {
        super(props);

        this.state = {
            show: false,
            displayMode: ComponentElementDisplayMode.INIT,
            wrappedCompState: {
                value: []
            },
            wrappedFormState: {}
        };

        this._onChangeEvent = this._onChangeEvent.bind(this);
        this._onAddEvent = this._onAddEvent.bind(this);
        this._onRemoveEvent = this._onRemoveEvent.bind(this);
        this._focusHandler = this._focusHandler.bind(this);
        this._setRef = this._setRef.bind(this);
    }

    private _onChangeEvent(e: ComponentEventType): void {
        if (this.state.displayMode === ComponentElementDisplayMode.EDIT) {
            const newState = {
                wrappedCompState: {
                    ...this.state.wrappedCompState,
                    value: [...e.value]
                }
            };
            this.setState(newState, this._onChangeEventCallback);
        }
    }

    private _onAddEvent(e: ComponentEventType): void {
    }

    private _onRemoveEvent(e: ComponentEventType): void {
    }

    private _callbackSetState() {
        let unmaskedValue = [];
        if (this._multipleInputMask && this.state.wrappedCompState.value && this.state.wrappedCompState.value.length > 0) {
            unmaskedValue = this.state.wrappedCompState.value;
            if (unmaskedValue === undefined) {
                unmaskedValue = [];
            }
        }
        if (this.dataType && (this.dataType.indexOf(DataType.NUMBER) > -1 || this.dataType.indexOf(DataType.INTEGER) > -1)) {
            this.emit(unmaskedValue);
        } else {
            if (this.state.wrappedCompState.useUnmask) {
                this.emit(unmaskedValue);
            } else {
                this.emit(this.state.wrappedCompState.value);
            }
        }
    }

    private _onChangeEventCallback() {
        this._callbackSetState();
    }

    private _focusHandler(e?) {
        if (this.state.displayMode === ComponentElementDisplayMode.EDIT && !this.accessibility) {
            let result = [];
            this.setState((prevState) => {
                if (this._multipleInputMask) {
                    result = prevState.wrappedCompState.value.map((x, i) => {
                        this._multipleInputMask.input.value = x;
                        this._multipleInputMask.checkVal();
                        return this._multipleInputMask.input.value || x;
                    });
                }
                return {
                    wrappedCompState: {
                        ...prevState.wrappedCompState,
                        value: result
                    }
                };
            }, this._callbackSetState);
        }
    }

    componentDidUpdate(prevProps, prevState): void {
        if (this._multipleInputMask) {
            this._makeStyle();
        }
        if (this._multipleInputMask && this._multipleInputMask.input) {
            ComponentHelper.tooltip(this._multipleInputMask.input, this.state.wrappedCompState.tooltipAttribute, this._tooltip);
        }
    }

    shouldComponentUpdate(nextProps: Readonly<Props>, nextState: Readonly<State>, nextContext: any): boolean {
        return true;
    }

    componentWillUpdate(nextProps: Readonly<Props>, nextState: Readonly<State>) {
        this._removeCustom();
    }

    componentWillUnmount(): void {
        this._removeCustom();
        this._multipleInputMask = null;
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
        this.emitDidCatch(error);
    }

    emitDidCatch(error: Error | any): void {
        Debug.console.error(error);
    }

    render(): React.ReactNode {
        const {
            show,
            displayMode
        } = this.state;

        if (!show) {
            return null;
        } else {
            switch (displayMode) {
                case ComponentElementDisplayMode.EDIT:
                    return this._render4Edit();
                case ComponentElementDisplayMode.READONLY:
                    return this._render4ReadOnly();
                case ComponentElementDisplayMode.DISABLED:
                    return this._render4Disabled();
                case ComponentElementDisplayMode.PRINT:
                    return this._render4Print();
                default:
                    return null;
            }
        }
    }

    private _render(newProps: Props): React.ReactNode {
        delete(newProps["printClassName"]);
        delete(newProps["printStyle"]);
        return (
            <MultipleInputMask
                {...newProps}
                style={null}
                className={null}
                key={this.elementId}
                id={this.elementId}
                unmask={this.state.wrappedCompState.useUnmask}
                autoClear={false}
                mask={ newProps.mask }
                accessibility={this.accessibility}
                ref={this._setRef}
                focusInput={this._focusHandler}
            />
        );
    }

    private _render4Edit(): React.ReactNode {
        const newProps = {
            ...this.state.wrappedCompState
        };
        return this._render(newProps);
    }

    private _render4ReadOnly(): React.ReactNode {
        const newProps = {
            ...this.state.wrappedCompState,
            readOnly: true
        };
        return this._render(newProps);
    }

    private _render4Disabled(): React.ReactNode {
        const newProps = {
            ...this.state.wrappedCompState,
            disabled: true
        };
        return this._render(newProps);
    }

    private _render4Print(): React.ReactNode {
        return (
            <div ref={this._setRef} style={this.state.wrappedCompState ? {wordBreak: "break-all", ...this.state.wrappedCompState.printStyle} : null} className={`bf-print-mode ${this.state.wrappedCompState ? this.state.wrappedCompState.printClassName : null}`}>{this.state.wrappedCompState.value && this.state.wrappedCompState.value.length > 0 ? this.state.wrappedCompState.value.join(",") : ""}</div>
        );
    }

    private _setRef(ref) {
        if (ref) {
            this._multipleInputMask = ref;
            this._makeStyle();
            this.readyComponent();
        }
    }

    private _removeCustom(): void {
        ComponentHelper.unbindTooltip(this._tooltip);
    }

    private _makeStyle(newProps = {...this.state.wrappedCompState}): void {
        if (this.state.displayMode === ComponentElementDisplayMode.EDIT || this.state.displayMode === ComponentElementDisplayMode.READONLY || this.state.displayMode === ComponentElementDisplayMode.DISABLED) {
            if (this._multipleInputMask && this._multipleInputMask.element) {
                let ul = this._multipleInputMask.element.querySelector("ul");
                ul.setAttribute("style", "");
                for (let item in newProps.style) {
                    ul.style[item] = newProps.style[item];
                }
            }
        }
    }
}